import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { inject, Injectable, DestroyRef } from "@angular/core";

import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { forkJoin, Observable, of, take } from "rxjs";
import { ITranslateObject, TaxprepT1Card, TranslateObject } from "src/app/views-interfaces";
import { CardDisplayService } from "../../shared/card-display/card-display.service";
import { CardViewService } from "../../shared/card-view-service/card-view.service";
import { CountersService, StatisticModel } from "../../shared/counters-service/counters.service";
import { isiFirmTaxDashboard } from "../../views-interfaces/dashboard-model.interface";
import { titleFromCard } from "../card-pipe/card-title.pipe";
import { CategoriesService } from "../categories-service/categories.service";
import { DashboardsService } from "../dashboards-service/dashboards.service";
import { FiltersService } from "../filters-service/filters.service";
import { HelpersService } from "../helpers-service/helpers.service";

@Injectable({ providedIn: "root" })
export class PrinterCardsService {
    private readonly destroyRef = inject(DestroyRef);
    private readonly cardDisplayService = inject(CardDisplayService);
    private readonly cardViewService = inject(CardViewService);
    private readonly categoriesService = inject(CategoriesService);
    private readonly countersService = inject(CountersService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly filtersService = inject(FiltersService);
    private readonly helpersService = inject(HelpersService);
    private readonly translate = inject(TranslateService);

    private readonly labels = {
        Filters: "",
        notStartedReport: "",
        completedReport: "",
        name: "",
        FamilyMember: "",
        LastYearReceivedDate: "",
        Category: "",
        ViewStep: "",
        AllNotStartedReturns: "",
        AllCompletedReturns: "",
        ThisYearCompletionDate: "",
        LastYearCompletionDate: "",
        AverageWorkCycleTime: "",
        WorkCycleTime: "",
        NoFilters: "",
        CardModalDays: "",
        Colon: "",
        Title: "",
        SortBy: "",
    };

    private board = {};

    storedCards: Record<string, TaxprepT1Card>;
    state: string;
    filtersArray = [];
    statusIdsToLoad = [];
    currentLangCode = "en-CA";
    isiFirmDashboard: boolean;
    showLastYearReceivedDateColumn: boolean;

    constructor() {
        const translate = this.translate;

        this.currentLangCode = translate.getFallbackLang();
        this.initLabels();
        translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: LangChangeEvent) => {
            this.currentLangCode = event.lang;
            this.initLabels();
        });
    }

    initLabels() {
        this.translate
            .get("Dashboard.Filters")
            .pipe(take(1))
            .subscribe((message: string) => {
                this.labels.Filters = message;
            });
        this.translate
            .get("Printers.reportHeaders.notStartedReport")
            .pipe(take(1))
            .subscribe((message: string) => {
                this.labels.notStartedReport = message;
            });
        this.translate
            .get("Printers.reportHeaders.completedReport")
            .pipe(take(1))
            .subscribe((message: string) => {
                this.labels.completedReport = message;
            });
        this.translate
            .get("General.Name")
            .pipe(take(1))
            .subscribe((message: string) => {
                this.labels.name = message;
            });
        this.translate
            .get("Board.FamilyMember")
            .pipe(take(1))
            .subscribe((message: string) => {
                this.labels.FamilyMember = message;
            });
        this.translate
            .get("NotStarted.LastYearReceivedDate")
            .pipe(take(1))
            .subscribe((message: string) => {
                this.labels.LastYearReceivedDate = message;
            });
        this.translate
            .get("Dashboard.Category")
            .pipe(take(1))
            .subscribe((message: string) => {
                this.labels.Category = message;
            });
        this.translate
            .get("ViewComponent.ViewStep")
            .pipe(take(1))
            .subscribe((message: string) => {
                this.labels.ViewStep = message;
            });
        this.translate
            .get("Dashboard.AllNotStartedReturns")
            .pipe(take(1))
            .subscribe((message: string) => {
                this.labels.AllNotStartedReturns = message;
            });
        this.translate
            .get("Dashboard.AllCompletedReturns")
            .pipe(take(1))
            .subscribe((message: string) => {
                this.labels.AllCompletedReturns = message;
            });
        this.translate
            .get("CompletedView.ThisYearCompletionDate")
            .pipe(take(1))
            .subscribe((message: string) => {
                this.labels.ThisYearCompletionDate = message;
            });
        this.translate
            .get("CompletedView.LastYearCompletionDate")
            .pipe(take(1))
            .subscribe((message: string) => {
                this.labels.LastYearCompletionDate = message;
            });
        this.translate
            .get("CompletedView.WorkCycleTime")
            .pipe(take(1))
            .subscribe((message: string) => {
                this.labels.WorkCycleTime = message;
            });
        this.translate
            .get("CompletedView.AverageWorkCycleTime")
            .pipe(take(1))
            .subscribe((message: string) => {
                this.labels.AverageWorkCycleTime = message;
            });
        this.translate
            .get("CardModal.Days")
            .pipe(take(1))
            .subscribe((message: string) => {
                this.labels.CardModalDays = message;
            });
        this.translate
            .get("FiltersSidebar.Labels.NoFilters")
            .pipe(take(1))
            .subscribe((message: string) => {
                this.labels.NoFilters = message;
            });
        this.translate
            .get("General.Colon")
            .pipe(take(1))
            .subscribe((message: string) => {
                this.labels.Colon = message;
            });
        this.translate
            .get("Board")
            .pipe(take(1))
            .subscribe((message: string) => {
                this.board = message;
            });
        this.translate
            .get("General.Title")
            .pipe(take(1))
            .subscribe((message: string) => {
                this.labels.Title = message;
            });
        this.translate
            .get("FiltersSidebar.Labels.SortBy")
            .pipe(take(1))
            .subscribe((message: string) => {
                this.labels.SortBy = message;
            });
    }

    async getAllSteps() {
        const steps = (await this.cardDisplayService.getModel())
            .filter((model) => model.state === this.state)
            .reduce((steps, model) => {
                if (!steps.some((it) => it.id === model.stepId)) {
                    steps.push({
                        id: model.stepId,
                        columnName: model.stepName,
                    });
                }

                return steps;
            }, []);
        if (this.state === "completed") {
            return [
                {
                    id: "all_steps_completed",
                    columnName: "Dashboard.AllCompletedReturns",
                },
                ...steps,
            ];
        } else if (this.state === "notStarted") {
            return [
                {
                    id: "all_steps_not_started",
                    columnName: "Dashboard.AllNotStartedReturns",
                },
                ...steps,
            ];
        } else {
            return steps;
        }
    }

    async renderSteps() {
        const selectStep = (id) => {
            return id === this.cardViewService.selectedStepId ? "selected" : "";
        };
        let steps = `<div class="steps"><span class="title">${this.labels.ViewStep}: </span><ul>`;
        (await this.getAllSteps()).forEach((step) => {
            if (step.columnName === "Dashboard.AllNotStartedReturns") {
                steps += `<li class='${selectStep(step.id)}'>${this.labels.AllNotStartedReturns}</li>`;
            } else if (step.columnName === "Dashboard.AllCompletedReturns") {
                steps += `<li class='${selectStep(step.id)}'>${this.labels.AllCompletedReturns}</li>`;
            } else {
                steps += `<li class='${selectStep(step.id)}'>${step.columnName[this.currentLangCode]}</li>`;
            }
        });
        steps += `</ul></div>`;
        return steps;
    }

    async renredHtml() {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        this.isiFirmDashboard = isiFirmTaxDashboard(currentDashboard);
        this.showLastYearReceivedDateColumn = this.isiFirmDashboard && currentDashboard.TaxYear >= 2025;

        if (!this.helpersService.categoriesInitialized) {
            await this.helpersService.initializeCategories();
        }
        const pageLayout = `
                ${this.state !== "notStarted" && this.state !== "completed" ? this.setLanscapeMod() : ""}
                ${this.renderStyles()}
                ${this.renderHeader(currentDashboard)}
                ${await this.renderSteps()}
                ${this.renderTableHeader()}
                ${await this.renderTableBody(currentDashboard)}
                ${this.renderFooter()}
                `;
        const div = window.document.createElement("div");
        div.className = "print_page_layout";
        div.innerHTML = pageLayout;
        const body = document.body;
        body.appendChild(div);
        window.print();
        body.removeChild(div);
    }

    getTranslatedString(value: string | ITranslateObject): Observable<string> {
        if (typeof value === "string") {
            return this.translate.get(value);
        } else {
            return of(value[this.currentLangCode]);
        }
    }

    getFilters() {
        this.filtersArray = [];
        if (this.filtersService.activeSortForPrint) {
            this.filtersArray.push([this.labels.SortBy, this.filtersService.activeSortForPrint]);
        }

        if (this.filtersService.activeFiltersForPrint && this.filtersService.activeFiltersForPrint.size > 0) {
            const length = this.filtersService.activeFiltersForPrint.size + 1;
            this.filtersService.activeFiltersForPrint.forEach((activeFilter) => {
                this.getTranslatedString(activeFilter.label)
                    .pipe(take(1))
                    .subscribe((message: string) => {
                        forkJoin(activeFilter.values.map((value) => this.getTranslatedString(value)))
                            .pipe(take(1))
                            .subscribe((values: string[]) => {
                                this.filtersArray.push([message, values.join(", ")]);
                                if (this.filtersArray.length === length) {
                                    this.renredHtml();
                                }
                            });
                    });
            });
        } else {
            this.renredHtml();
        }
    }

    setLanscapeMod() {
        return `<style type="text/css" media="print">
                    @page { size: landscape; }
                </style>`;
    }

    printData(storedCards, state, statusIdsToLoad) {
        this.storedCards = storedCards;
        this.state = state;
        this.statusIdsToLoad = statusIdsToLoad;
        this.getFilters();
    }

    renderFilters() {
        let filters = ``;
        if (this.filtersArray.length > 0) {
            this.filtersArray.forEach((filter) => {
                if (filter[1]) {
                    filters += `<li>${filter[0]}: <span> ${filter[1]} </span> </li>`;
                } else {
                    filters += `<li>${filter[0]} </li>`;
                }
            });
        } else {
            filters = `<li>${this.labels.NoFilters} </li>`;
        }

        return filters;
    }

    private async getCategory(card: TaxprepT1Card, currentDashboard) {
        let category = this.categoriesService.getCategory(card.cardId);
        const categoryIsValid = category && card.categoryId !== "Unassigned";
        if (!categoryIsValid) {
            const assignCategoryNoTags = currentDashboard.TaxYear < 2025 ? currentDashboard.AssignCategoryNoTags : currentDashboard.AssignCategoryId;
            const categoriesFromService = await this.categoriesService.getCategoriesList();
            category = this.categoriesService.calculateCategoryWithCategories(categoriesFromService, assignCategoryNoTags, card.tags);
        }
        return category;
    }

    async renderTableBody(currentDashboard) {
        let rows = "";
        for (const item of Object.keys(this.storedCards)) {
            if (item) {
                let htmlRow = ``;
                let complexity: TranslateObject | string;
                const calculatedCategory = this.cardDisplayService.handleCardComplexity(this.storedCards[item])?.Name;
                if (calculatedCategory) {
                    complexity = calculatedCategory;
                } else {
                    const categoryResult = await this.getCategory(this.storedCards[item], currentDashboard);
                    if (categoryResult) {
                        complexity = categoryResult.Name;
                    } else {
                        complexity = "";
                    }
                }

                if (this.state === "notStarted") {
                    let lastYearDocumentsReceivedDate = "";
                    let documentReceivedLate = false;
                    if (this.storedCards[item].lastYearDocumentsReceivedDate) {
                        lastYearDocumentsReceivedDate = this.helpersService.dateUnix(
                            this.storedCards[item].lastYearDocumentsReceivedDate,
                            "MMMM d, yyyy",
                        );
                    }
                    if (lastYearDocumentsReceivedDate) {
                        documentReceivedLate =
                            this.storedCards[item].lastYearDocumentsReceivedDate + HelpersService.secondsInAYear <=
                                this.helpersService.convertMillisecondsToSeconds(Date.now()) && !this.storedCards[item].documentsReceivedDate;
                    }

                    if (this.isiFirmDashboard) {
                        htmlRow = `<div class="row">
                        <div class="col-4">
                            <span class="taxpayer-count">${this.helpersService.getClientFileCount(this.storedCards[item])}</span>
                            ${this.isVIP(this.storedCards[item].isVIP)}
                            ${documentReceivedLate ? '<i class="fa fa-exclamation-triangle"></i>' : ""}
                            ${titleFromCard(this.storedCards[item])}
                        </div>
                        <div class="col-2"><span>${this.board[this.storedCards[item].return.type]}</span></div>
                        ${this.showLastYearReceivedDateColumn ? `<div class="col-2">${lastYearDocumentsReceivedDate}</div>` : ""}
                        <div class="col-2">${complexity[this.currentLangCode] || ""}</div>
                    </div>`;
                    } else {
                        htmlRow = `<div class="row">
                        <div class="col-4">
                            <span class="taxpayer-count">${this.helpersService.getClientFileCount(this.storedCards[item])}</span>
                            ${this.isVIP(this.storedCards[item].isVIP)}
                            ${documentReceivedLate ? '<i class="fa fa-exclamation-triangle"></i>' : ""}
                            ${titleFromCard(this.storedCards[item])}
                        </div>
                        <div class="col-2"><span>${this.board[this.storedCards[item].return.type]}</span></div>
                        <div class="col-2">${lastYearDocumentsReceivedDate}</div>
                        <div class="col-2">${complexity[this.currentLangCode] || ""}</div>
                    </div>`;
                    }

                    rows += htmlRow;
                } else {
                    const DaysSinceFields = this.cardDisplayService.getDaysSinceFields(this.storedCards[item], this.statusIdsToLoad);
                    const isEFileStatusAlert = this.helpersService.checkCardEFileStatusAlert(this.storedCards[item]);
                    htmlRow = `<div class="row">
                        <div class="col-2">
                            <span class="taxpayer-count">${this.helpersService.getClientFileCount(this.storedCards[item])}</span>
                            ${this.isVIP(this.storedCards[item].isVIP)}
                            ${isEFileStatusAlert ? '<i class="fa fa-exclamation-triangle"></i>' : ""}
                            ${titleFromCard(this.storedCards[item])}
                        </div>
                        <div class="col-2"><span>${this.board[this.storedCards[item].return.type] || ""}</span></div>
                        <div class="col-2">${DaysSinceFields.thisYearCompletionDate || ""}</div>
                        <div class="col-2">${DaysSinceFields.lastYearCompletedDate || ""}</div>
                        <div class="col-1">${complexity[this.currentLangCode] || ""}</div>
                        <div class="col-1">${DaysSinceFields.daysSinceReady} ${this.labels.CardModalDays}</div>
                    </div>`;
                    rows += htmlRow;
                }
            }
        }
        return rows;
    }

    renderTableHeader() {
        if (this.state === "notStarted") {
            if (this.isiFirmDashboard) {
                return `
                <div class="print_layout_header row not_started">
                    <div class="col-4"><i class="fa fa-user taxpayer-count"></i> ${this.labels.name}</div>
                    <div class="col-2">${this.labels.FamilyMember}</div>
                    ${this.showLastYearReceivedDateColumn ? `<div class="col-2">${this.labels.LastYearReceivedDate}</div>` : ""}
                    <div class="col-2">${this.labels.Category}</div>
                </div>
            `;
            } else {
                return `
            <div class="print_layout_header row not_started">
                <div class="col-4"><i class="fa fa-user taxpayer-count"></i> ${this.labels.name}</div>
                <div class="col-2">${this.labels.FamilyMember}</div>
                <div class="col-2">${this.labels.LastYearReceivedDate}</div>
                <div class="col-2">${this.labels.Category}</div>
            </div>
        `;
            }
        } else {
            return `
            <div class="print_layout_header row completed">
                <div class="col-2"><i class="fa fa-user taxpayer-count"></i> ${this.labels.name}</div>
                <div class="col-2">${this.labels.FamilyMember}</div>
                <div class="col-2">${this.labels.ThisYearCompletionDate}</div>
                <div class="col-2">${this.labels.LastYearCompletionDate}</div>
                <div class="col-1">${this.labels.Category}</div>
                <div class="col-1">${this.labels.WorkCycleTime}</div>
            </div>
        `;
        }
    }

    renderStyles() {
        return `
            <style type="text/css">
                .print_page_layout{
                    padding: 25px;
                }
                .print_page_layout .page_header .date{
                    text-transform: capitalize;
                }
                .html,body{
                    height: auto !important;
                }
                .wk-app__root{
                    display: none !important;
                }
                .print_layout_header{
                    font-size: 16px;
                }
                .print_page_layout .filters {
                    display: flex;
                }
                .print_page_layout .col-4 {
                    float: left;
                    width: 38%;
                    padding: 0 3px;
                    min-height: 10px;
                }
                .print_page_layout .col-2 {
                    float: left;
                    width: 19%;
                    padding: 0 3px;
                    min-height: 10px;
                }
                .print_page_layout .col-1 {
                    float: left;
                    width: 10%;
                    padding: 0 3px;
                    min-height: 10px;
                }
                .print_page_layout .row div > *{
                    display: inline-block;
                }
                .print_page_layout .row {
                    border-bottom: 1px solid #D8D8D8;
                    padding: 10px 0;
                    word-wrap: break-word;
                    font-size: 14px;
                    margin: 0;
                }
                .print_page_layout h1{
                    text-align: center;
                }
                .print_page_layout .filters .title{
                    font-weight: bold;
                    font-size: 16px;
                    display: inline-block;
                }
                .print_page_layout li {
                    list-style: none;
                    display: inline-block;
                    padding-left: 30px;
                }
                .print_page_layout ul{
                    display: inline-block;
                    padding: 0;
                }
                .print_page_layout .date {
                    float: right;
                }
                .print_page_layout .header > div {
                    vertical-align: top
                    font-size: 14px;
                }
                .print_page_layout .steps{
                    display: flex;
                }
                .print_page_layout .steps .title{
                        flex: none;
                }
                .print_page_layout .steps ul{
                    padding-left: 20px;
                }
                .print_page_layout .steps li{
                    border: 1px solid #c1c0c0;
                    padding: 5px 10px;
                    margin-right: 20px;
                    margin-bottom: 10px;
                    color: #c1c0c0;
                }
                .print_page_layout .wk-card__head-vip {
                    font-weight: bold;
                }
                .print_page_layout .steps .selected{
                    border-color: #000;
                    color: #000;
                }
                .footer{
                    padding: 15px 0;
                    font-size: 14px;
                }
            </style>
        `;
    }

    renderHeader(currentDashboard) {
        const dashboardTitle = currentDashboard.Title;
        const date = new Date();
        const todaysDate = date.toLocaleString(this.helpersService.getLang(), {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
        });
        return `
            <div class="page_header">
                <span class="date">${todaysDate}</span>
                <h3>${this.labels.Title}</h3>
                <span>${dashboardTitle}</span>
                <h1>${this.labels[this.state + "Report"]}</h1>
                <div class="filters">
                    <div class="title">${this.labels.Filters}:</div>
                    <ul>
                        ${this.renderFilters()}
                    </ul>
                </div>
            </div>
        `;
    }

    isVIP(isVIP) {
        return isVIP ? `<div class="wk-card__head-vip"> VIP </div>` : "";
    }

    renderFooter() {
        const models = this.countersService.statisticsModel.filter(
            (model) =>
                model.stepId === this.cardViewService.selectedStepId ||
                (this.cardViewService.selectedStepId === "all_steps_completed" && model.state === "completed") ||
                (this.cardViewService.selectedStepId === "all_steps_not_started" && model.state === "notStarted"),
        );
        const count = models.reduce((sum, model) => sum + model.count, 0);
        return `
            <div class="footer">
                <div class="col-md-8">
                    <i class="fa fa-user taxpayer-count"></i>
                    ${count} ${this.board["Returns"]}
                </div>
                <div class="col-md-4">
                    ${this.renderAverageDaysInFlow(models, count)}
                </div>
            </div>
        `;
    }

    renderAverageDaysInFlow(models: StatisticModel[], count: number) {
        if (this.state === "completed") {
            const totalDaysInWorkflow = models.reduce((sum, model) => sum + model.totalDaysInWorkflow, 0);
            return `<span class="pull-right cycle-time">
                        ${this.labels.AverageWorkCycleTime}${this.labels.Colon} ${Math.ceil(totalDaysInWorkflow / count)}
                    </span>`;
        } else {
            return "";
        }
    }
}
